import { useEffect, useMemo, useRef, useState } from "react";
import {
  NavWrap,
  NavInner,
  Left,
  LogoWrap,
  Right,
  IconBtn,
  NavList,
  NavItem,
  NavLink,
  MeniuBtn,
  MobilePanel,
  MobileList,
  MobileItem,
  MobileLink,
  BrandText,
  LangSwitcherWrap,
  FloatLang,
  Spacer,
  PhoneBtn,
  PhonePanel,
  PhoneList,
  FloatPhoneWrap,
  FloatPhonePanel,
  FloatPhoneBtn,
} from "./MenuBarStyles";
import Logo from "../../assets/images/Logo.png";
import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";
import ReservationModal from "../ReservationModal/ReservationModal";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";

// 👇 contextul cu alegerea userului (delivery/restaurant)
import { useExperience } from "../../context/ExperienceContext";

const MOBILE_BP = 768;

export default function MenuBar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const isHome = location.pathname === "/acasa";

  // 👇 citim/setăm alegerea globală
  const { experience, setExperience } = useExperience();

  // 👇 dacă userul a intrat direct pe o pagină de meniu, setăm contextul
  useEffect(() => {
    if (location.pathname.startsWith("/meniu_restaurant")) {
      if (experience !== "restaurant") setExperience("restaurant");
    } else if (location.pathname.startsWith("/meniu_delivery")) {
      if (experience !== "delivery") setExperience("delivery");
    }
  }, [location.pathname, experience, setExperience]);

  // 👇 deducem „experience” efectiv (context > URL > fallback)
  const effectiveExperience = useMemo(() => {
    if (experience) return experience;
    if (location.pathname.startsWith("/meniu_restaurant")) return "restaurant";
    if (location.pathname.startsWith("/meniu_delivery")) return "delivery";
    return null;
  }, [experience, location.pathname]);

  // 👇 path-ul pentru Meniu
  const menuPath =
    effectiveExperience === "restaurant"
      ? "/meniu_restaurant/"
      : "/meniu_delivery/";

  // 👇 LINKS dinamic
  const LINKS = useMemo(
    () => [
      { to: "/acasa", label: t("nav.home") },
      { to: menuPath, label: t("nav.menu") },
      { to: "/contact/", label: t("nav.contact") },
      { to: "/despre_noi/", label: t("nav.about") },
    ],
    [t, menuPath]
  );

  const phoneNumbers = useMemo(
    () => ["+40 268 456 156", "+40 721 456 156", "+40 766 456 156"],
    []
  );

  const [phase, setPhase] = useState("overlay");
  const [open, setOpen] = useState(false);
  const [resOpen, setResOpen] = useState(false);

  // phone dropdown (desktop, în navbar)
  const [phoneOpen, setPhoneOpen] = useState(false);
  const phoneBtnRef = useRef(null);
  const phonePanelRef = useRef(null);

  // area for click-away (menu)
  const menuAreaRef = useRef(null);

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(`(max-width: ${MOBILE_BP}px)`).matches;
  });

  // vizibilitatea „bulinelor” (doar pentru RO/EN – rămâne ca înainte)
  const [floatVisible, setFloatVisible] = useState(true);
  const lastYRef = useRef(0);
  const hideTimerRef = useRef(null);

  const phaseForUI = isHome && !isMobile ? phase : "solid";

  // urmărește trecerea mobil/desktop
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(`(max-width: ${MOBILE_BP}px)`);
    const onChange = () => setIsMobile(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    onChange();
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    const onResize = () => {
      setOpen(false);
      setPhoneOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // phase pe scroll
  useEffect(() => {
    const THRESHOLD = 30;
    if (!isHome) {
      setPhase("solid");
      return;
    }
    const onScroll = () => {
      if (window.matchMedia(`(max-width: ${MOBILE_BP}px)`).matches) {
        setPhase("solid");
        return;
      }
      const y = window.scrollY;
      setPhase(y > THRESHOLD ? "solid" : "overlay");
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);

  useEffect(() => {
    if (!isHome) setPhase("solid");
    else setPhase(window.scrollY > 30 ? "solid" : "overlay");
  }, [isHome]);

  // 🔵 mobil: RO/EN ca înainte — ascundere la scroll în jos
  useEffect(() => {
    if (!isMobile) return;
    const onScroll = () => {
      const y = window.scrollY;
      const goingDown = y > (lastYRef.current || 0);
      setFloatVisible(!goingDown);
      lastYRef.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isMobile]);

  // 🔵 mobil: RO/EN ca înainte — auto-hide după 4s
  useEffect(() => {
    if (!isMobile) return;
    const kick = () => {
      setFloatVisible(true);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => setFloatVisible(false), 4000);
    };
    kick();
    window.addEventListener("touchstart", kick, { passive: true });
    window.addEventListener("mousemove", kick);
    return () => {
      window.removeEventListener("touchstart", kick);
      window.removeEventListener("mousemove", kick);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [isMobile]);

  // click-away pentru meniul mobil
  useEffect(() => {
    const onClickAway = (e) => {
      if (!menuAreaRef.current) return;
      if (!menuAreaRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("click", onClickAway);
    return () => document.removeEventListener("click", onClickAway);
  }, [open]);

  // click-away pentru dropdown-ul de telefon (navbar desktop)
  useEffect(() => {
    const onClickAway = (e) => {
      if (!phoneOpen) return;
      const btnOk = phoneBtnRef.current?.contains(e.target);
      const panelOk = phonePanelRef.current?.contains(e.target);
      if (!btnOk && !panelOk) setPhoneOpen(false);
    };
    if (phoneOpen) document.addEventListener("click", onClickAway);
    return () => document.removeEventListener("click", onClickAway);
  }, [phoneOpen]);

  // 🔵 ca înainte: când meniul mobil e deschis, „bulinele” se ascund (doar RO/EN)
  useEffect(() => {
    if (open && isMobile) setFloatVisible(false);
  }, [open, isMobile]);

  const handleReservationSubmit = async (form) => {
    const response = await fetch(
      process.env.REACT_APP_GRAPHQL_URL ||
        "https://friendzburger.onrender.com/graphql",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: `
            mutation CreateRezervare(
              $nume: String!,$email: String,$telefon: String!,
              $persoane: Int!,$data: String!,$ora: String!,$mentiuni: String
            ) {
              createRezervare(
                nume:$nume,email:$email,telefon:$telefon,
                persoane:$persoane,data:$data,ora:$ora,mentiuni:$mentiuni
              ){ message id }
            }`,
          variables: {
            nume: form.nume,
            email: form.email,
            telefon: form.telefon,
            persoane: form.persoane,
            data: form.data,
            ora: form.ora,
            mentiuni: form.mentiuni,
          },
        }),
      }
    );
    const result = await response.json();
    if (result.errors) {
      console.error("Eroare GraphQL:", result.errors);
      throw new Error("Nu s-a putut salva rezervarea.");
    }
    console.log(result.data.createRezervare.message);
  };

  const orderNowLabel =
    i18n.resolvedLanguage === "ro" ? "Comandă acum" : "Order now";

  return (
    <>
      <NavWrap $phase={phaseForUI}>
        <NavInner $phase={phaseForUI}>
          <Left>
            <LogoWrap href="/acasa" aria-label={t("brand")} $phase={phaseForUI}>
              <img src={Logo} alt={t("brand")} />
              <BrandText $phase={phaseForUI}>{t("brandFull")}</BrandText>
            </LogoWrap>
          </Left>

          <NavList>
            {LINKS.map((l) => (
              <NavItem key={l.to}>
                <NavLink href={l.to} $phase={phaseForUI}>
                  {l.label}
                </NavLink>
              </NavItem>
            ))}
          </NavList>

          <Right ref={menuAreaRef}>
            {/* ---- Desktop: Language + Call Now + Rezervă + Meniu ---- */}
            <LangSwitcherWrap>
              <LanguageSwitcher />
            </LangSwitcherWrap>

            {/* Call Now (desktop – navbar) */}
            <div style={{ position: "relative" }}>
              <PhoneBtn
                ref={phoneBtnRef}
                type="button"
                $phase={phaseForUI}
                aria-haspopup="true"
                aria-expanded={phoneOpen}
                onClick={() => setPhoneOpen((v) => !v)}
                title={`${
                  i18n.resolvedLanguage === "ro" ? "Sună acum" : "Call now"
                }`}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.11 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.9.31 1.77.57 2.61a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.47-1.14a2 2 0 0 1 2.11-.45c.84.26 1.71.45 2.61.57A2 2 0 0 1 22 16.92Z"
                    fill="currentColor"
                  />
                </svg>
                <span>
                  {i18n.resolvedLanguage === "ro" ? "Sună acum" : "Call now"}
                </span>
              </PhoneBtn>

              <PhonePanel ref={phonePanelRef} $open={phoneOpen}>
                <PhoneList>
                  {phoneNumbers.map((n) => (
                    <li key={n}>
                      <a
                        href={`tel:${n.replace(/\s+/g, "")}`}
                        onClick={() => setPhoneOpen(false)}
                      >
                        {n}
                      </a>
                    </li>
                  ))}
                </PhoneList>
              </PhonePanel>
            </div>

            <IconBtn
              aria-label="Rezervă masă"
              title="Rezervă masă"
              type="button"
              $phase={phaseForUI}
              onClick={() => setResOpen(true)}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  d="M6 4h12v16l-6-4-6 4V4z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
              </svg>
            </IconBtn>

            <MeniuBtn
              aria-label="Deschide meniul"
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              title="Meniu"
              type="button"
              $phase={phaseForUI}
            >
              <span />
              <span />
              <span />
            </MeniuBtn>

            <MobilePanel $open={open}>
              <MobileList>
                {LINKS.map((l) => (
                  <MobileItem key={l.to}>
                    <MobileLink href={l.to} onClick={() => setOpen(false)}>
                      {l.label}
                    </MobileLink>
                  </MobileItem>
                ))}
              </MobileList>
            </MobilePanel>
          </Right>
        </NavInner>

        {/* 🔵 Bulina RO/EN — EXACT ca înainte, DOAR pe mobil, cu auto-hide */}
        {isMobile &&
          createPortal(
            <FloatLang $visible={floatVisible}>
              <LanguageSwitcher />
            </FloatLang>,
            document.body
          )}

        {/* 🟢 Bulina „Sună/Comandă acum” — doar pentru DELIVERY */}
        {effectiveExperience === "delivery" &&
          createPortal(
            <MobilePhoneFloat
              numbers={phoneNumbers}
              visible={true}
              label={orderNowLabel}
            />,
            document.body
          )}
      </NavWrap>

      <Spacer $phase={phaseForUI} />

      <ReservationModal
        isOpen={resOpen}
        onClose={() => setResOpen(false)}
        onSubmit={handleReservationSubmit}
      />
    </>
  );
}

/** Componentă internă pentru bulina de telefon flotantă (cu panou + „Comandă acum”) */
function MobilePhoneFloat({ numbers, visible, label }) {
  const [open, setOpen] = useState(false);

  // închide lista la scroll (butonul rămâne vizibil)
  useEffect(() => {
    const onScroll = () => setOpen(false);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // click-away pentru panou
  useEffect(() => {
    const onClickAway = (e) => {
      setOpen((prev) => {
        if (!prev) return prev;
        const btn = document.getElementById("float-phone-btn");
        const panel = document.getElementById("float-phone-panel");
        if (btn?.contains(e.target) || panel?.contains(e.target)) return prev;
        return false;
      });
    };
    if (open) document.addEventListener("click", onClickAway);
    return () => document.removeEventListener("click", onClickAway);
  }, [open]);

  return (
    <FloatPhoneWrap $visible={visible}>
      <FloatPhoneBtn
        id="float-phone-btn"
        aria-expanded={open}
        aria-controls="float-phone-panel"
        onClick={() => setOpen((v) => !v)}
        title={label}
      >
        {/* icon telefon (stânga) */}
        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.11 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.9.31 1.77.57 2.61a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.47-1.14a2 2 0 0 1 2.11-.45c.84.26 1.71.45 2.61.57A2 2 0 0 1 22 16.92Z"
            fill="currentColor"
          />
        </svg>

        {/* eticheta „Comandă acum” / „Order now” */}
        <span className="label">{label}</span>
      </FloatPhoneBtn>

      <FloatPhonePanel id="float-phone-panel" $open={open}>
        {numbers.map((n) => (
          <a
            key={n}
            href={`tel:${n.replace(/\s+/g, "")}`}
            onClick={() => setOpen(false)}
          >
            {n}
          </a>
        ))}
      </FloatPhonePanel>
    </FloatPhoneWrap>
  );
}
